import { useLocalStorageState } from "ahooks";
import dayjs from "dayjs";
import { cloneDeep, debounce, isEmpty, pickBy } from "lodash";

import { useCallback, useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { createSearchParams, useNavigate, useSearchParams } from "react-router-dom";

import FlightIcon from "@mui/icons-material/Flight";
import FlightTakeoffIcon from "@mui/icons-material/FlightTakeoff";
import TableRowsRoundedIcon from "@mui/icons-material/TableRowsRounded";
import ViewHeadlineIcon from "@mui/icons-material/ViewHeadline";
import {
  Accordion,
  AccordionSummary,
  Grid,
  IconButton,
  LinearProgress,
  ToggleButton,
  ToggleButtonGroup,
  Typography,
} from "@mui/material";
import { GridExpandMoreIcon } from "@mui/x-data-grid";

import { ApiGetFlightRoutesSampleParams } from "@/api/routes";
import {
  GetRoutesFlightsParams,
  getRoutesFlights,
  getRoutesFlightsCashRate,
  getRoutesFlightsCount,
} from "@/api/routesFlights";
import DateFastForward from "@/components/DateFastForward";
import FlightList from "@/components/FlightList";
import FlightListSkeleton from "@/components/FlightList/Skeleton";
import FlightListV2 from "@/components/FlightListV2";
import IntroRoutes from "@/components/IntroRoutes";
import { ScrollTopBar } from "@/components/ScrollTopBar";
import { ROUTE_NUMBER_COUNT } from "@/constants/config";
import { ROUTES_PAGE_INIT_LOADDATA } from "@/constants/eventName";
import { tranfer_bank_to_program } from "@/constants/programs";
import { TimePoints } from "@/constants/timeRange";
import { useGlobalStoreContext } from "@/context/globalStoreContext";
import useEvent from "@/hooks/useEvent";
import { useListenEvent } from "@/hooks/useEventEmitter";
import usePlatform from "@/hooks/usePlatform";
import useThemeData from "@/hooks/useThemeData";
import { ConditionType, createSearchQuery } from "@/pages/Routes/hooks/useCondition";
import { alertPanoramaFilterStorage } from "@/storage/alert-panorama-filter";
import { CabinsType, FlightItem, FlightItem as FlightItemType } from "@/typings";

import FlightDetailsModal from "../Routes/components//FlightDetails";
import MobileSearchBar from "../Routes/components/MobileSearchBar";
import SearchBar from "../Routes/components/SearchBar";
import useCheckCondition from "../Routes/hooks/useCheckCondition";
import { LocationEnum } from "../Routes/hooks/useLocationFilter";
import CashFareRange from "./components/CashFareRange";
import FilterBar from "./components/FilterBar";
import Sort from "./components/Sort";

const RoutesFlights = () => {
  const theme = useThemeData();
  const { isMobile, isPc } = usePlatform();
  const { checkCondition } = useCheckCondition();
  const store = useGlobalStoreContext();
  const showUpsell: boolean = store?.show_upsell;

  const navigate = useNavigate();

  const [open, setOpen] = useState(false);
  const [currentParams, setCurrentParams] = useState<
    ApiGetFlightRoutesSampleParams & { type: CabinsType }
  >();

  const [listType, setListType] = useLocalStorageState("routes-flights-list-view-type", {
    defaultValue: 2,
  });

  const closestAirportRef = useRef(store?.closest_airport);
  closestAirportRef.current = store?.closest_airport;

  const [condition, setCondition] = useState<ConditionType>();
  const [searchData, setSearchData] = useState<FlightItemType[]>([]);
  const [isLoading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);

  const pageInfoRef = useRef({ page_size: 50, page: 1 });
  const [hasMore, setHasMore] = useState(true);

  const routesAlertParams = useRef(alertPanoramaFilterStorage.getValue());

  const [fetchParams, setFetchParams] = useState({
    sort_by: routesAlertParams.current ? "created_at" : "max_points",
  });
  const [cashRate, setCashRate] = useState({ min_cash_rate: 0, max_cash_rate: 0 });

  const handleSearch = useEvent(async (data: ConditionType, isClick: boolean) => {
    if (data) {
      const errMsg = checkCondition(data);

      if (errMsg) {
        setLoading(false);
        return;
      }

      const nextParams = createSearchQuery(data);

      const nextSearch = createSearchParams(nextParams).toString();

      if (isClick) {
        if (data.searchMode === "true") {
          navigate(`/routes-flights?${nextSearch}`, { replace: true });
        } else {
          navigate(`/routes?${nextSearch}`, { replace: true });
        }
      }

      // setSearchParams(nextSearch);
      setCondition(data);

      const fromLocation = {
        regions: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.REGION)
          .map((ele) => ele.code),

        countries: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.COUNTRY)
          .map((ele) => ele.code),

        airports: data.fromLocation
          .filter((ele) => ele.type === LocationEnum.AIRPORT)
          .map((ele) => ele.code),
      };
      const toLocation = {
        regions: data.toLocation
          .filter((ele) => ele.type === LocationEnum.REGION)
          .map((ele) => ele.code),

        countries: data.toLocation
          .filter((ele) => ele.type === LocationEnum.COUNTRY)
          .map((ele) => ele.code),

        airports: data.toLocation
          .filter((ele) => ele.type === LocationEnum.AIRPORT)
          .map((ele) => ele.code),
      };

      const params = {
        date_range: {
          from: dayjs(data.startDate).format("YYYY-MM-DD"),
          to: dayjs(data.endDate).format("YYYY-MM-DD"),
        },
        origins: fromLocation,
        arrivals: toLocation,
        cabin: data.cabins.split("&").map((item) => item.trim()),
      };

      filterChange(params);
    }
  });

  const filterChange = useCallback((params) => {
    setFetchParams((prev) => {
      return {
        ...prev,
        ...params,
      };
    });
  }, []);

  useListenEvent(ROUTES_PAGE_INIT_LOADDATA, (data: ConditionType) => {
    if (data.fromLocation.length !== 0 && data.toLocation.length !== 0) {
      setCondition(data);
      handleSearch(data);
    }
  });

  const transformParams = useCallback((params) => {
    const newParams = pickBy(params, (value) => {
      return value;
    });

    const fields = ["stops", "maxPoints", "tax", "duration", "seats"];

    for (const field of fields) {
      if (newParams[field]) {
        if (field === "duration") {
          newParams[field] = {
            operator: "soe",
            value: Math.round(Number(newParams[field]) * 60),
          };
        } else if (field == "seats") {
          newParams[field] = {
            operator: "lge",
            value: Number(newParams[field]),
          };
        } else {
          newParams[field] = {
            operator: "soe",
            value: Number(newParams[field]),
          };
        }
      }
    }

    // for (const item of ["arrivalTimeRange", "departureTimeRange"]) {
    //   newParams[item] = {
    //     min: TimePoints[newParams[item][0]],
    //     max: TimePoints[newParams[item][1]],
    //   };
    // }

    newParams["arrivalTimeRange"] = {
      min: TimePoints[newParams["times"][0]],
      max: TimePoints[newParams["times"][1]],
    };
    newParams["departureTimeRange"] = {
      min: TimePoints[newParams["times"][2]],
      max: TimePoints[newParams["times"][3]],
    };

    delete newParams["times"];

    const banksToProgram = newParams["banks"]?.map((key) => tranfer_bank_to_program.get(key));

    newParams["programs"] = Array.from(
      new Set([...newParams["programs"], ...banksToProgram.flat()])
    );

    if (!newParams["programs"].length) {
      delete newParams["programs"];
    }

    if (!newParams["banks"].length) {
      delete newParams["banks"];
    }

    // delete newParams["arrivalTimeRange"];
    // delete newParams["departureTimeRange"];

    return newParams;
  }, []);

  const getFlightData = useCallback(
    async (params) => {
      try {
        console.log(
          "%c [  ]-201",
          "font-size:13px; background:pink; color:#bf2c9f;",
          transformParams(cloneDeep(params))
        );
        setLoading(true);
        const res = await getRoutesFlights(
          transformParams(cloneDeep(params)) as GetRoutesFlightsParams
        );

        setLoading(false);
        setSearchData([...searchData, ...res.data]);

        setHasMore(!res.is_last_page);
      } catch (error) {
        if (error instanceof Error) {
          console.error(error);
        }
      } finally {
        setLoading(false);
      }
    },
    [transformParams, searchData]
  );

  const getTotalCount = useCallback(
    async (params) => {
      const res = await getRoutesFlightsCount(
        transformParams(cloneDeep(params)) as GetRoutesFlightsParams
      );

      setTotalCount(res.data);
    },
    [transformParams]
  );

  const getCashRate = useCallback(
    async (params) => {
      const res = await getRoutesFlightsCashRate(
        transformParams(cloneDeep(params)) as GetRoutesFlightsParams
      );

      setCashRate(res.data);
    },
    [transformParams]
  );

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const fetchDataFn = useCallback(
    debounce(async (params) => {
      setSearchData([]);
      await Promise.all([
        getFlightData({ ...params, ...pageInfoRef.current }),
        getTotalCount({ ...params, ...pageInfoRef.current }),
        getCashRate({ ...params }),
      ]);
    }, 1000),
    []
  );

  const onOpenRouteDialog = useEvent((v: FlightItem) => {
    setCurrentParams({
      program: v.program_code,
      origin: v.fare.products[0].origin,
      arrival: v.fare.products[v.fare.products.length - 1].destination,
      date: dayjs(v.date).format("YYYY-MM-DD"),
      type: v.cabin_type,
    });

    setOpen(true);
  });

  useEffect(() => {
    // 全局的alertParams用完即清除
    return () => {
      alertPanoramaFilterStorage.destroyValue();
    };
  }, []);

  useEffect(() => {
    if (!isEmpty(fetchParams) && fetchParams.arrivals && fetchParams.origins) {
      console.log("%c [  ]-246", "font-size:13px; background:pink; color:#bf2c9f;", fetchParams);
      pageInfoRef.current = {
        page: 1,
        page_size: pageInfoRef.current.page_size,
      };
      fetchDataFn(fetchParams);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fetchParams]);

  return (
    <>
      <Helmet>
        <html lang="en" />
        <title>AwardTool | Routes-Flight Search</title>
        <meta
          name="description"
          content={
            "Search across unlimited regions, cities, and airports with 90-day range for " +
            ROUTE_NUMBER_COUNT.toLocaleString() +
            "+ popular routes."
          }
        />
      </Helmet>
      <div className="max-w-[1380px] mx-auto mode-bg">
        {isMobile ? (
          <>
            <MobileSearchBar data={condition} onSearch={handleSearch} isLoading={isLoading} />
            <div className="px-[10px] mt-[10px]">
              <FilterBar onChange={filterChange} initValues={routesAlertParams.current} />
            </div>
          </>
        ) : (
          <div className="px-[10px]">
            <div className="pt-[16px]">
              <SearchBar onSearch={handleSearch} isLoading={isLoading} />
            </div>

            <FilterBar onChange={filterChange} initValues={routesAlertParams.current} />
          </div>
        )}

        <div className="px-[10px] mt-[20px]">{<IntroRoutes useWhere="alert-routes-flights" />}</div>
        {isLoading ? (
          <div className="mt-[13px] mx-[10px]">
            <LinearProgress />
          </div>
        ) : null}

        {searchData.length === 0 && !isLoading ? (
          <div className="mt-[80px] text-center">
            <Typography color="textSecondary">No results match your current filters.</Typography>
            <Typography color="textSecondary">
              Consider modify your filter setting or search criteria
            </Typography>
            <img src={theme.emptyBg} alt="" className="h-[200px] mt-[30px]" />
          </div>
        ) : (
          <div className="px-[10px]">
            {searchData.length ? (
              <>
                <Grid
                  container
                  className={
                    isMobile ? "w-full pt-[15x] pb-[10px]" : "w-full pt-[15x] mt-[15px] pb-[10px]"
                  }
                  alignItems="center"
                >
                  <Grid item xs={6} md={6}>
                    <div className="flex items-center flex-1">
                      <Typography color="textPrimary">
                        {totalCount.toLocaleString()} flights
                      </Typography>
                      {isPc ? (
                        <ToggleButtonGroup
                          size="small"
                          value={listType}
                          exclusive
                          onChange={(e, v) => {
                            setListType(v);
                          }}
                          aria-label="text alignment"
                          sx={{
                            marginLeft: "10px",
                            ".MuiToggleButton-root": {
                              padding: "3px",
                            },
                          }}
                        >
                          <ToggleButton value={1} aria-label="list">
                            <TableRowsRoundedIcon />
                          </ToggleButton>
                          <ToggleButton value={2} aria-label="table">
                            <ViewHeadlineIcon />
                          </ToggleButton>
                        </ToggleButtonGroup>
                      ) : null}
                    </div>
                  </Grid>

                  <Grid item xs={6} md={6} sx={{ textAlign: "right" }}>
                    <div className="flex items-center justify-end">
                      {isPc && cashRate.min_cash_rate && (
                        <CashFareRange
                          min_cash_rate={cashRate.min_cash_rate}
                          max_cash_rate={cashRate.max_cash_rate}
                        />
                      )}

                      {isPc && <DateFastForward />}

                      <Sort
                        value={fetchParams.sort_by}
                        onChange={(v) => {
                          filterChange({
                            sort_by: v,
                          });
                        }}
                      />
                    </div>
                  </Grid>
                </Grid>
                {isMobile && (
                  <div className="flex justify-between items-center w-full mb-[0px] mt-[-3px]">
                    <>
                      {isMobile && cashRate.min_cash_rate && (
                        <CashFareRange
                          min_cash_rate={cashRate.min_cash_rate}
                          max_cash_rate={cashRate.max_cash_rate}
                        />
                      )}
                      {isMobile && (
                        <div className="relative flex justify-end ml-[auto] pb-[10px] mt-[-2px]">
                          <DateFastForward />
                        </div>
                      )}
                    </>
                  </div>
                )}

                {listType === 1 || isMobile ? (
                  <FlightList
                    needMore={false}
                    showPro={showUpsell}
                    data={searchData}
                    showSearchBtn={false}
                    needRouteBtn
                    onOpenRouteDialog={onOpenRouteDialog}
                  />
                ) : (
                  <FlightListV2
                    needMore={false}
                    data={searchData}
                    onOpenRouteDialog={onOpenRouteDialog}
                  />
                )}
              </>
            ) : null}

            {isLoading ? (
              <FlightListSkeleton showPro={showUpsell} count={searchData.length} />
            ) : null}
          </div>
        )}

        <div className="px-[10px]">
          {!isLoading && hasMore ? (
            <Accordion
              expanded={false}
              sx={{
                // boxShadow: "none",
                marginTop: "-2px",
                border: (theme) => `1px solid ${theme.palette.divider}`,
              }}
            >
              <AccordionSummary onClick={(e) => e.stopPropagation()}>
                <div
                  className="w-full flex items-center justify-center"
                  onClick={() => {
                    pageInfoRef.current = {
                      ...pageInfoRef.current,
                      page: pageInfoRef.current.page + 1,
                    };
                    getFlightData({ ...fetchParams, ...pageInfoRef.current });
                  }}
                >
                  <IconButton color="primary">
                    <GridExpandMoreIcon />
                  </IconButton>
                  <Typography color="textPrimary" className="!pr-[4px]">
                    More Flights{" "}
                  </Typography>
                  <FlightTakeoffIcon className="ml-[3px]" />
                </div>
              </AccordionSummary>
            </Accordion>
          ) : // <div
          //   className="w-full flex items-center cursor-pointer"
          //   onClick={() => {
          //     pageInfoRef.current = {
          //       ...pageInfoRef.current,
          //       page: pageInfoRef.current.page + 1,
          //     };
          //     getFlightData({ ...fetchParams, ...pageInfoRef.current });
          //   }}
          // >
          //   <IconButton color="primary">
          //     <GridExpandMoreIcon />
          //   </IconButton>
          //   <Typography color="textPrimary">More Flights</Typography>
          // </div>
          null}

          {isLoading ? <FlightListSkeleton count={3} /> : null}
        </div>
      </div>

      <ScrollTopBar />

      {open && (
        <FlightDetailsModal open={open} onClose={() => setOpen(false)} params={currentParams} />
      )}
    </>
  );
};

export default RoutesFlights;
